import { useEffect, useRef, useCallback, useMemo } from 'react'
import MindElixir from 'mind-elixir'
import { useThemeStore, themes } from '@/stores/theme'
import './mindmap.css'

export type MindMapNode = {
  id: string
  topic: string
  children?: MindMapNode[]
  expanded?: boolean
  branchColor?: string
  tags?: string[]
  icons?: string[]
}

interface MindMapViewProps {
  data: MindMapNode
  height?: number | string
  readonly?: boolean
}

/** hex → rgba（思维导图库需要真实色值） */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export default function MindMapView({ data, height = '60vh', readonly = true }: MindMapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mindRef = useRef<any>(null)
  const initializedRef = useRef(false)
  // 跟随主题 / 暗色切换
  const { current, isDark } = useThemeStore()
  const themeColors = useMemo(
    () => (themes.find((t) => t.key === current) || themes[0]).colors,
    [current],
  )

  const initMind = useCallback(() => {
    if (!containerRef.current) return

    // 销毁旧实例
    if (mindRef.current) {
      try { mindRef.current.destroy() } catch (e) { console.error('[MindMapView] 操作失败:', e) }
      mindRef.current = null
    }

    const mind = new (MindElixir as any)({
      el: containerRef.current,
      direction: (MindElixir as any).RIGHT,
      editable: !readonly,
      contextMenu: !readonly,
      toolBar: false,
      nodeMenu: !readonly,
      keypress: !readonly,
      allowUndo: false,
      draggable: false,
      overflowHidden: false,
      newTopicName: '新节点',
      theme: {
        name: 'TestPlatform',
        type: 'light',
        palette: [themeColors.primary, '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
        cssVar: {
          '--node-gap-x': '36px',
          '--node-gap-y': '6px',
          '--main-gap-x': '50px',
          '--main-gap-y': '12px',
          '--main-color': '#fff',
          '--main-bgcolor': themeColors.primary,
          '--main-bgcolor-transparent': hexToRgba(themeColors.primary, 0.12),
          '--color': isDark ? '#cbd5e1' : '#3d405b',
          '--bgcolor': isDark ? '#1e293b' : '#f4faf6',
          '--selected': themeColors.primary,
          '--accent-color': themeColors.primary,
          '--root-color': '#fff',
          '--root-bgcolor': themeColors.primaryDark,
          '--root-border-color': themeColors.primaryDark,
          '--root-radius': '10px',
          '--main-radius': '8px',
          '--topic-padding': '8px 20px',
          '--panel-color': isDark ? '#f1f5f9' : '#fff',
          '--panel-bgcolor': isDark ? '#1e293b' : '#fff',
          '--panel-border-color': isDark ? 'rgba(51, 65, 85, 0.5)' : '#e8e4dc',
          '--map-padding': '30px',
        },
      },
    })

    try {
      const result = mind.init({ nodeData: data })
      if (result instanceof Error) {
        console.error('Mind map init error:', result)
      }
      mindRef.current = mind
      initializedRef.current = true
    } catch (err) {
      console.error('Mind map init exception:', err)
    }
  }, [data, readonly, themeColors, isDark])

  useEffect(() => {
    // 延迟初始化确保 DOM 已渲染
    const timer = setTimeout(initMind, 100)
    return () => {
      clearTimeout(timer)
      if (mindRef.current) {
        try { mindRef.current.destroy() } catch (e) { console.error('[MindMapView] 操作失败:', e) }
        mindRef.current = null
      }
    }
  }, [initMind])

  // 数据变化时刷新
  useEffect(() => {
    if (mindRef.current && initializedRef.current) {
      try {
        mindRef.current.refresh({ nodeData: data })
      } catch (e) { console.error('[MindMapView] 操作失败:', e) }
    }
  }, [data])

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: typeof height === 'number' ? `${height}px` : height,
        overflow: 'hidden',
        borderRadius: 8,
        border: '1px solid var(--au-border)',
      }}
    />
  )
}
